import { cloneElement, forwardRef, isValidElement } from 'react';
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  isDisabled?: boolean;
  asChild?: boolean;
}

const sizeClasses = {
  sm: 'h-7 gap-1.5 px-2.5 text-sm',
  md: 'h-9 gap-2 px-3.5 text-sm',
  lg: 'h-11 gap-2.5 px-4.5 text-base',
} as const;

const spinnerSizeClasses = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-[18px] w-[18px]',
} as const;

const variantClasses = {
  primary:
    'bg-[color:var(--color-primary)] text-[color:var(--color-text-inverse)] hover:brightness-95 active:brightness-90',
  secondary:
    'border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-hover-overlay)] active:bg-[color:var(--color-active-overlay)]',
  ghost:
    'bg-transparent text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-hover-overlay)] active:bg-[color:var(--color-active-overlay)]',
  danger:
    'bg-[color:var(--color-sla-breached-solid)] text-[color:var(--color-text-inverse)] hover:brightness-95 active:brightness-90',
  outline:
    'border border-[color:var(--color-border-strong)] bg-transparent text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-hover-overlay)] active:bg-[color:var(--color-active-overlay)]',
} as const;

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leadingIcon,
    trailingIcon,
    isDisabled = false,
    asChild = false,
    className,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const isBlocked = isDisabled || isLoading;
  const icon = isLoading ? <Loader2 className={cn('animate-spin motion-reduce:animate-none', spinnerSizeClasses[size])} aria-hidden="true" /> : leadingIcon;

  const classes = cn(
    'inline-flex select-none items-center justify-center whitespace-nowrap rounded-lg font-medium',
    'transition-colors transition-shadow duration-150 motion-reduce:transition-none motion-reduce:active:translate-y-0',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-focus-ring-offset)]',
    (variant === 'primary' || variant === 'danger') && 'focus-visible:shadow-focus',
    'disabled:cursor-not-allowed disabled:opacity-50',
    sizeClasses[size],
    variantClasses[variant],
    className,
  );

  const content = (
    <>
      {icon}
      {size === 'sm' && isLoading ? <span className="sr-only">Loading</span> : <span>{children}</span>}
      {!isLoading ? trailingIcon : null}
    </>
  );

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (isBlocked) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  if (asChild && isValidElement(children)) {
    const childElement = children as React.ReactElement<{
      className?: string;
      onClick?: (clickEvent: React.MouseEvent<HTMLElement>) => void;
      'aria-busy'?: React.AriaAttributes['aria-busy'];
      'aria-disabled'?: React.AriaAttributes['aria-disabled'];
    }>;
    return cloneElement(childElement, {
      className: cn(classes, childElement.props.className),
      'aria-busy': isLoading ? 'true' : undefined,
      'aria-disabled': isBlocked ? 'true' : undefined,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        if (isBlocked) {
          event.preventDefault();
          return;
        }

        childElement.props.onClick?.(event);
      },
    });
  }

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      disabled={isBlocked}
      aria-busy={isLoading ? 'true' : undefined}
      aria-disabled={isBlocked ? 'true' : undefined}
      onClick={handleClick}
      {...rest}
    >
      {content}
    </button>
  );
});
